import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquareWarning, UserRound } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const COMPLAINTS_TAB = '/feedback?tab=complaints';

export function RaiseComplaintPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), COMPLAINTS_TAB);
  const initialPatientId = params.get('patientId') ?? '';
  const [form, setForm] = useState({ patientId: initialPatientId, subject: '', body: '', slaHours: 48 });
  const [error, setError] = useState<string | null>(null);

  const raise = useMutation({
    mutationFn: () =>
      post('/feedback/complaints', {
        patientId: form.patientId || null,
        subject: form.subject,
        body: form.body,
        slaHours: form.slaHours,
      }),
    onSuccess: () => {
      toast.success('Complaint logged', 'The SLA clock has started');
      void queryClient.invalidateQueries({ queryKey: ['complaints'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const valid = form.subject.trim().length >= 3 && form.body.trim().length >= 3;
  const dirty = !raise.isSuccess && (form.patientId !== initialPatientId || form.subject !== '' || form.body !== '' || form.slaHours !== 48);

  return (
    <FormScreen
      title="Log a complaint"
      icon={MessageSquareWarning}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        raise.mutate();
      }}
    >
      <FormSection title={tr('opd', 'patient')} icon={UserRound} columns={1}>
        <Field label="Patient" hint="Optional">
          <PatientPicker value={form.patientId} onChange={(patientId) => setForm({ ...form, patientId })} />
        </Field>
      </FormSection>

      <FormSection title={tr('opd', 'complaint')} icon={MessageSquareWarning}>
        <Field label="Subject" required>
          <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        </Field>
        <Field label="SLA (hours)">
          <Input type="number" min={1} value={form.slaHours} onChange={(e) => setForm({ ...form, slaHours: Number(e.target.value) })} />
        </Field>
        <Field label="Detail" required className="sm:col-span-2">
          <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Log" loading={raise.isPending} incomplete={!valid} cancelTo={returnTo} />
    </FormScreen>
  );
}
